//* 주문 알림(결제완료/배송완료) 재발송 모달 — 구매자 정보 수정과 무관하게 언제든 재발송
//* 상태 검증은 백엔드가 최종 수행: paid=Paid/Confirmed만, delivered=Delivered만, 취소 주문 400.
//* (스펙: docs/store-guest-info-update-spec.md · 404 안내는 구서버 안전장치)

import { FC, useCallback, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  resendShopOrderAlert,
  ShopOrderAlertType,
} from '@shared/api/shopOrder/shopOrder.controller';
import { ShopOrderForSeller } from '@shared/api/shopOrder/shopOrder.interface';
import { Button } from '@shared/components/Button';
import Modal from '@shared/components/Modal';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { useAuth } from '@shared/state/hooks/useAuth';
import { DeliveryState, PaymentState } from '@shared/types';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

interface Props {
  openId: string;
  order: ShopOrderForSeller;
}

const statusOf = (e: unknown): number | undefined =>
  (e as { response?: { status?: number } })?.response?.status;

//* 주문 상태 기준 재발송 가능 여부 (버튼 노출 조건 — 카드에서도 동일 기준으로 사용)
export const canResendOrderAlert = (order: ShopOrderForSeller) => {
  const notCanceled = ![PaymentState.CANCELED, PaymentState.CANCEL_REQUESTED].includes(
    order.paymentDetail.status,
  );
  const paid =
    notCanceled &&
    [PaymentState.PAID, PaymentState.CONFIRMED].includes(order.paymentDetail.status);
  const delivered =
    notCanceled && order.deliveryDetail.status === DeliveryState.DELIVERED;
  return { paid, delivered, any: paid || delivered };
};

export const OrderAlertResendModal: FC<Props> = function OrderAlertResendModal({
  openId,
  order,
}: Props) {
  const { sellerRole } = useAuth();
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));
  const notifiacationInstance = useRecoilValue(notificationInstanceAtom);
  const [resending, setResending] = useState<ShopOrderAlertType | null>(null);

  const onCloseModal = useCallback(() => {
    setOpen(false);
    history.back();
  }, [setOpen]);

  const { paid: canResendPaid, delivered: canResendDelivered } =
    canResendOrderAlert(order);

  //* 발송 대상 = 게스트 계정의 번호 (백엔드가 user를 다시 populate해서 발송)
  const guest = order.user;
  const targetName = guest?.info?.name ?? order.deliveryDetail.senderName;
  const targetPhone = guest?.info?.phoneNumber ?? order.deliveryDetail.senderPhoneNumber;

  const onResend = useCallback(
    async (type: ShopOrderAlertType) => {
      setResending(type);
      try {
        await resendShopOrderAlert(order._id, type, sellerRole());
        notificateSuccess(
          notifiacationInstance,
          type === 'paid' ? '결제완료 알림 재발송 완료' : '배송완료 알림 재발송 완료',
        );
      } catch (err) {
        console.error(err);
        const status = statusOf(err);
        notificateError(
          notifiacationInstance,
          status === 400
            ? '주문 상태상 재발송할 수 없습니다.'
            : status === 404
            ? '주문을 찾을 수 없거나 아직 서버에 반영되지 않은 기능입니다.'
            : '알림 재발송에 실패했습니다.',
        );
      } finally {
        setResending(null);
      }
    },
    [notifiacationInstance, order._id, sellerRole],
  );

  return (
    <Modal openId={openId}>
      <div className="space-y-4">
        <div>
          <p className="text-lg font-bold">알림 재발송</p>
          <p className="break-keep pt-1 text-sm text-gray-600">
            구매자에게 결제완료/배송완료 알림(알림톡·문자)을 다시 보냅니다.
          </p>
        </div>

        <div className="rounded-md border bg-gray-50 p-3 text-sm">
          <p className="font-medium">발송 대상</p>
          <p className="text-gray-700">
            {targetName} · {phoneNumberStringFormator(targetPhone)}
          </p>
          <p className="break-keep pt-1 text-xs text-gray-500">
            번호가 틀렸다면 먼저 [수정]으로 구매자 정보를 고친 뒤 재발송해주세요.
          </p>
        </div>

        {/* 두 버튼 항상 노출, 상태상 불가능한 쪽은 disabled */}
        <div className="space-y-1.5">
          <div className="flex gap-2">
            <Button
              className="h-10 flex-1 border border-gray-300 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!canResendPaid || resending !== null}
              onClick={() => onResend('paid')}
            >
              {resending === 'paid' ? '발송 중...' : '결제완료 알림 재발송'}
            </Button>
            <Button
              className="h-10 flex-1 border border-gray-300 text-sm disabled:cursor-not-allowed disabled:opacity-40"
              disabled={!canResendDelivered || resending !== null}
              onClick={() => onResend('delivered')}
            >
              {resending === 'delivered' ? '발송 중...' : '배송완료 알림 재발송'}
            </Button>
          </div>
          {!canResendPaid && (
            <p className="break-keep text-xs text-gray-500">
              · 결제완료 알림은 결제완료/구매확정 주문만 보낼 수 있습니다. (취소 주문
              불가)
            </p>
          )}
          {!canResendDelivered && (
            <p className="break-keep text-xs text-gray-500">
              · 배송완료 알림은 배송 완료 후에만 보낼 수 있습니다.
            </p>
          )}
        </div>

        <Button
          className="button-rectangle min-h-[40px] border border-gray-300"
          onClick={onCloseModal}
        >
          닫기
        </Button>
      </div>
    </Modal>
  );
};
